import * as React from 'react';

import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';

import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import ArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import ArrowRight from '@mui/icons-material/KeyboardArrowRight';
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import DoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft';
import DoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight';

// Suppression d'éléments d'une liste
const not = (a, b) => {
    return a.filter((elem) => b.find(e => e.id !== elem.id));
}

// Décocher des éléments d'une liste
const unckeck = (a, b) => {
    return a.filter((elem) => b.find(e => e !== elem.id));
}

// Maj des éléments cochés dans une liste
const intersection = (a, b) => {
    return a.filter((elem) => b.find(e => e === elem.id));
}

// Tri par ordre alphabétique d'une liste
const sortRightList = (list) => {
    if (list.length === 0) return [];

    return Array.from(list).sort((a, b) => {
        return a.libelle.localeCompare(b.libelle, 'fr', { sensitivity: 'base' });
    });
}

// Init des valeurs des flèches
const arrowUp = -1;
const arrowDown = 1

const TransferList = ({ listeGauche, listeDroite, onChange }) => {

    // Hook éléments sélectionnés
    const [checked, setChecked] = React.useState([]);

    // Hook liste de gauche
    const [left, setLeft] = React.useState([]);

    // Hook liste de droite 
    const [right, setRight] = React.useState([]);

    // Eléments sélectionnés de chaque côté
    const leftChecked = intersection(left, checked);
    const rightChecked = intersection(right, checked);

    // Alimentation des actions
    React.useEffect(() => {
        setLeft(listeGauche);
        setRight(listeDroite);

    }, [listeGauche, listeGauche])

    // Maj des ids d'actions
    const updateActionsIds = (list) => {
        const ids = list.map(x => x.id);
        onChange(ids);
    }

    // Handler d'un élément sélectionné
    const handleToggle = (id) => () => {
        const currentIndex = checked.indexOf(id);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(id);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    // Handler tout déplacer à droite
    const handleAllRight = () => {
        const concatList = right.concat(left);
        setRight(sortRightList(concatList));

        setLeft([]);
        updateActionsIds([]);
    };

    // Handler déplacement d'éléments sélectionnés vers la droite
    const handleCheckedRight = () => {
        const concatList = right.concat(leftChecked);
        setRight(sortRightList(concatList));

        const leftList = not(left, leftChecked);
        setLeft(leftList);

        setChecked(unckeck(checked, leftChecked));
        setChecked(unckeck(checked, rightChecked));

        updateActionsIds(leftList);
    };

    // Handler déplacement d'éléments sélectionnés vers la gauche
    const handleCheckedLeft = () => {
        const concatList = left.concat(rightChecked);
        setLeft(concatList);

        setRight(not(right, rightChecked));

        setChecked(unckeck(checked, rightChecked));
        setChecked(unckeck(checked, leftChecked));

        updateActionsIds(concatList);
    };

    // Handler tout déplacer à gauche
    const handleAllLeft = () => {
        const concatList = left.concat(right);
        setLeft(concatList);

        setRight([]);

        updateActionsIds(concatList);
    };

    // Handler de tri liste de gauche
    const handleMoveItems = (id, direction) => {
        const position = left.findIndex((e) => e.id === id);

        if (position < 0) {
            throw new Error("Elément introuvable")
        }
        else if ((direction === arrowUp && position === 0) || (direction === arrowDown && position === left.length - 1)) {
            return console.log("Mouvement impossible")
        }

        // On conserve l'élément à bouger
        const itemPosition = left[position];

        // On l'enlève du tableau
        const filterItems = left.filter((e) => e.id !== id);

        // On l'ajout avec son nouvel index
        filterItems.splice(position + direction, 0, itemPosition)

        // On sauvegarde la liste modifiée
        setLeft(filterItems);
        updateActionsIds(filterItems);
    }

    const customList = (liste, showSortArrows) => (
        <Paper elevation={5} sx={{ height: 500, overflow: 'auto' }}>

            <List component="div" role="list">
                {liste.length > 0 &&
                    liste.map((elem) => {
                        const labelId = `transfer-list-item-${elem.id}-label`;

                        return (
                            <ListItem
                                key={elem.id}
                                role="listitem"
                            >
                                <ListItemIcon>
                                    <Checkbox
                                        onClick={handleToggle(elem.id)}
                                        checked={checked.indexOf(elem.id) !== -1}
                                        tabIndex={-1}
                                        disableRipple
                                        inputProps={{
                                            'aria-labelledby': labelId,
                                        }}
                                    />
                                </ListItemIcon>

                                <ListItemText id={labelId} primary={elem.libelle} />

                                {showSortArrows && (
                                    <>
                                        <Button
                                            variant="outlined"
                                            sx={{ p: 0 }}
                                            size="small"
                                            onClick={() => handleMoveItems(elem.id, arrowUp)}
                                            aria-label="move up"
                                        >
                                            <ArrowUpIcon />
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            sx={{ p: 0 }}
                                            size="small"
                                            onClick={() => handleMoveItems(elem.id, arrowDown)}
                                            aria-label="move down"
                                        >
                                            <ArrowDownIcon />
                                        </Button>
                                    </>
                                )}
                            </ListItem>
                        );
                    })}
            </List>

        </Paper >
    );

    return (
        <Grid container justifyContent="space-between" alignItems="center">

            <Grid item xs={5}>
                {customList(left, true)}
            </Grid>

            <Grid item xs={1}>
                <Grid container direction="column" alignItems="center">
                    <Button
                        sx={{ my: 2 }}
                        variant="contained"
                        size="small"
                        onClick={() => handleAllRight()}
                        disabled={left.length === 0}
                        aria-label="move all right"
                    >
                        <DoubleArrowRight />
                    </Button>
                    <Button
                        sx={{ my: 2 }}
                        variant="contained"
                        size="small"
                        onClick={() => handleCheckedRight()}
                        disabled={leftChecked.length === 0}
                        aria-label="move selected right"
                    >
                        <ArrowRight />
                    </Button>
                    <Button
                        sx={{ my: 2 }}
                        variant="contained"
                        size="small"
                        onClick={() => handleCheckedLeft()}
                        disabled={rightChecked.length === 0}
                        aria-label="move selected left"
                    >
                        <ArrowLeft />
                    </Button>
                    <Button
                        sx={{ my: 2 }}
                        variant="contained"
                        size="small"
                        onClick={() => handleAllLeft()}
                        disabled={right.length === 0}
                        aria-label="move all left"
                    >
                        <DoubleArrowLeft />
                    </Button>
                </Grid>
            </Grid>

            <Grid item xs={5}>
                {customList(right, false)}
            </Grid>

        </Grid>
    );
}

export default TransferList;